<template>
    <DModal
        :show="showModal"
        :title="__(`tools.documents.samples.${document}.${sample}`) "
        is-big
        overflow-auto
        @close="closeModal"
    >
        <DRow>
            <DCol md="12">
                <DCard class="pt-0">
                    <DCardHeader>
                        <div
                            class="modal-header"
                        >
                            <label
                                class="fw-bold"
                                for="language"
                            >{{ __('tools.documents.modal.language') }}</label>
                            <DSelect
                                id="language"
                                v-model="language"
                                :options="languages"
                                @update:value="handleLanguageChange"
                            />
                            <label
                                class="fw-bold"
                                for="brand"
                            >{{ __('tools.documents.modal.brand') }}</label>
                            <DSelect
                                id="brand"
                                v-model="brand"
                                :options="brandOptions"
                                @update:value="updateSelectedBrand"
                            />
                            <label
                                class="fw-bold"
                                for="marketRegion"
                            >{{ __('tools.documents.modal.market_region') }}</label>
                            <DSelect
                                id="marketRegion"
                                v-model="marketRegion"
                                :options="marketRegions"
                            />
                            <DButton
                                :loading="loading"
                                icon="eye"
                                :label="__('tools.documents.modal.display')"
                                color="primary"
                                @click="displaySampleDocument()"
                            />
                            <DButton
                                v-if="pdfSource !== null"
                                icon="download"
                                :disabled="isInLoadingState"
                                :label="__('tools.documents.modal.download')"
                                @click="downloadSampleDocument()"
                            />
                        </div>
                    </DCardHeader>
                    <DCardBody class="overflow-hidden">
                        <pdf
                            v-for="i in numPages"
                            :key="i"
                            :src="pdfSource"
                            :page="i"
                        />
                        <div
                            v-if="errors.length > 0"
                        >
                            <p
                                v-for="(error, index) in errors"
                                :key="index"
                            >
                                <DAlert color="danger">
                                    {{ error }}
                                </DAlert>
                            </p>
                        </div>
                    </DCardBody>
                </DCard>
            </DCol>
        </DRow>
    </DModal>
</template>

<script>
import pdf from 'vue3-pdf'

import NProgress from 'nprogress'

export default {
    name: 'DocumentPreviewModal',
    components: {
        pdf
    },
    props: {
        showModal: {
            type: Boolean,
            default: false
        },
        document: {
            type: String,
            default: ''
        },
        sample: {
            type: String,
            default: ''
        },
        brands: {
            type: Array,
            default: () => { return [] }
        },
        availableLanguages: {
            type: Array,
            default: () => { return [] }
        },
        marketRegions: {
            type: Array,
            default: () => { return [] }
        }
    },
    emits: ['closeModal'],
    data () {
        return {
            brand: this.brands[0]?.id,
            marketRegion: this.marketRegions[0]?.value,
            loading: false,
            errors: [],
            pdfSource: null,
            numPages: null,
            fileName: null,
            language: null,
            languages: []
        }
    },
    computed: {
        brandOptions () {
            return this.brands.map(brand => {
                return {
                    value: brand.id,
                    label: brand.name
                }
            })
        }
    },
    mounted () {
        this.languages = this.getAvailableLanguages()
        this.language = this.getAvailableLanguages()[0].value
    },
    methods: {
        async displaySampleDocument () {
            NProgress.start()

            this.loading = true
            this.pdfSource = null
            this.errors = []
            this.numPages = null

            try {
                const response = await window.axios.post(this.$route('tools.documents.show-sample-document'), {
                    document: this.document,
                    sample: this.sample,
                    brand: this.brand,
                    language: this.language,
                    market_region: this.marketRegion
                }, {
                    responseType: 'blob'
                })

                NProgress.done()

                const blob = new Blob([response.data], { type: 'application/pdf' })

                this.pdfSource = URL.createObjectURL(blob)
                if (this.pdfSource) {
                    const pdfInstance = await pdf.createLoadingTask(this.pdfSource).promise
                    this.numPages = pdfInstance.numPages
                }
                this.loading = false
                this.fileName = response.headers['content-disposition'].match(/filename=(.+)/)[1]
            } catch (error) {
                this.loading = false
                NProgress.done()

                if (error.response && error.response.data) {
                    try {
                        const text = await error.response.data.text()
                        const response = JSON.parse(text)

                        Object.keys(response.errors).forEach((field) => {
                            response.errors[field].forEach((message) => {
                                this.errors.push(message)
                            })
                        })
                    } catch (parseError) {
                        this.errors.push('An error occurred while parsing the error response.')
                    }
                } else {
                    this.errors.push(error.message || 'An unknown error occurred.')
                }
            }
        },
        downloadSampleDocument () {
            const link = document.createElement('a')
            link.href = this.pdfSource
            link.download = this.fileName
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        closeModal () {
            this.pdfSource = null
            this.fileName = null

            this.$emit('closeModal')
        },
        updateSelectedBrand (item) {
            const brandId = parseInt(item)
            this.brand = brandId
            this.languages = this.getAvailableLanguages()
            this.language = this.brands.filter(brand => brand.id === brandId)[0].default_customer_language
        },
        handleLanguageChange (item) {
            this.language = item
        },
        getAvailableLanguages () {
            const lang = []

            this.availableLanguages.forEach((item) => {
                if (item.brand_id === this.brand) {
                    lang.push(item.languages)
                }
            })

            return lang[0]
        }
    }
}
</script>

<style lang="scss" scoped>
    .overflow-hidden {
        overflow: hidden !important;
    }
    .modal-header {
      display: flex;
      grid-gap: 15px;
      padding: 15px;
      align-items: center;
    }
</style>
